import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import sidebarData from '../../data/sidebar.json';
import './CompanyConfiguration.css';

const CompanyConfiguration = () => {
    const navigate = useNavigate();
    const [searchValue, setSearchValue] = useState("");
    const [groupedLinks, setGroupedLinks] = useState({});

    // Grouping mapping based on sidebar.json company setup links
    const groupDefinitions = {
        "Core Settings & Audit": ["Company Profile", "Company List", "DB Cofiguration", "DB Cofiguration List", "Company Module Mapping", "Company Module List", "View Audit Log Data"],
        "Location Setup": ["Add Continent", "Continent List", "Add Country", "Country List", "Add Region", "Region List", "Add Branch", "Branch List"],
        "Organization Setup": ["Add Role", "Role List", "Department Group", "Department Group List", "Control Department", "Control Department List", "Sub Control Department", "Sub Control Department List"],
        "Financial & IT Setup": ["Add Financial Year", "List Financial Year", "Add Financials Classification", "Financials Classification List", "Add Invoice Currency", "Invoice Currency List", "Add Cost Center", "View Cost Center", "Add Income Tax Master", "View Income Tax Master", "IT Master", "IT Master List", "Map IT Master", "Map IT Master List"],
        "Asset Definitions": ["Add Purpose", "Purpose List", "Other Purpose", "Other Purpose List", "Add Type Of Addition", "Type Of Addition List", "Add Unit Of Measure", "Unit Of Measure List", "Add Year Of Addition", "List Year Of Addition", "Add Repairs Name", "Repairs Name List"],
        "Ticket and Support": ["Add Ticket Subject", "View Ticket Subject", "Add Ticket Admin", "View Ticket Admin"],
        "RFID": ["Add RFID Master", "View RFID Master", "Add RFID Key", "View RFID Key"]
    };

    const getGroupName = (title) => {
        for (const [groupName, titles] of Object.entries(groupDefinitions)) {
            if (titles.includes(title)) {
                return groupName;
            }
        }
        return "Other Settings";
    };

    useEffect(() => {
        const companySetupNode = sidebarData.find(item => item.id === "10" || item.title === "Company setup");
        if (companySetupNode && companySetupNode.childrens) {
            const children = companySetupNode.childrens;

            const tempGroups = {};
            children.forEach(child => {
                const grp = getGroupName(child.title);
                if (!tempGroups[grp]) {
                    tempGroups[grp] = [];
                }
                tempGroups[grp].push(child);
            });
            setGroupedLinks(tempGroups);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const filteredGroups = () => {
        if (!searchValue) return groupedLinks;
        const result = {};
        Object.keys(groupedLinks).forEach(grp => {
            const filteredChildren = groupedLinks[grp].filter(child =>
                child.title.toLowerCase().includes(searchValue.toLowerCase())
            );
            if (filteredChildren.length > 0) {
                result[grp] = filteredChildren;
            }
        });
        return result;
    };

    const renderGroups = filteredGroups();

    return (
        <div className="container-fluid company-config-container p-4 fade-in">
            <div className="config-header pt-3 mb-4">
                <div className="d-flex flex-wrap justify-content-between align-items-center">
                    <div className="mb-3 mb-md-0">
                        <h3 className="mb-1 fw-bold text-primary" style={{ letterSpacing: "-0.5px" }}>
                            <i className="bi bi-buildings-fill me-2 fs-4"></i>
                            Company Configurations
                        </h3>
                        <p className="text-muted mb-0 config-subtitle">Centralized master data definitions and structural settings</p>
                    </div>
                    <div className="search-bar" style={{ minWidth: "300px" }}>
                        <div className="input-group shadow-sm rounded-pill custom-search-group">
                            <span className="input-group-text bg-white border-end-0 rounded-start-pill ps-4 text-primary">
                                <i className="bi bi-search"></i>
                            </span>
                            <input
                                type="text"
                                className="form-control border-start-0 rounded-end-pill py-2 shadow-none"
                                placeholder="Search settings (e.g. Roles, Regions)..."
                                value={searchValue}
                                onChange={(e) => setSearchValue(e.target.value)}
                            />
                        </div>
                    </div>
                </div>
            </div>

            <div className="row g-4 config-masonry">
                {Object.keys(renderGroups).map((groupName, idx) => (
                    <div className="col-md-6 col-lg-4 d-flex align-items-stretch" key={idx}>
                        <div className="card w-100 shadow-sm border-0 config-card rounded-4 overflow-hidden">
                            <div className="card-header bg-white border-bottom py-3 px-4 shadow-sm position-relative config-card-header">
                                <div className="card-header-bg"></div>
                                <h6 className="mb-0 fw-bold config-group-title text-dark position-relative z-1">
                                    {groupName}
                                </h6>
                            </div>
                            <div className="card-body p-2 bg-light">
                                <ul className="list-group list-group-flush config-list bg-transparent">
                                    {renderGroups[groupName].map((link, lidx) => (
                                        <li
                                            key={lidx}
                                            className="list-group-item bg-transparent d-flex align-items-center p-3 border-0 config-list-item rounded-3 mb-1"
                                            onClick={() => navigate(link.path)}
                                        >
                                            <div className="config-icon-box shadow-sm me-3 d-flex justify-content-center align-items-center rounded bg-white text-primary">
                                                {link.img_icon ? (
                                                    <img src={link.img_icon} alt={link.title} style={{ width: '18px' }} />
                                                ) : (
                                                    <i className={`${link.icon} fs-6`}></i>
                                                )}
                                            </div>
                                            <div className="flex-grow-1">
                                                <h6 className="mb-0 text-dark config-link-title fw-semibold" style={{ fontSize: '14px' }}>{link.title}</h6>
                                            </div>
                                            <div className="config-arrow">
                                                <i className="bi bi-chevron-right text-muted small"></i>
                                            </div>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        </div>
                    </div>
                ))}

                {Object.keys(renderGroups).length === 0 && (
                    <div className="col-12 text-center py-5">
                        <div className="empty-state-icon mb-3">
                            <i className="bi bi-search display-3 text-secondary opacity-50"></i>
                        </div>
                        <h4 className="text-muted fw-bold">No configuration matches found</h4>
                        <p className="text-secondary">Try adjusting your search terms or view all settings.</p>
                        <button className="btn btn-outline-primary rounded-pill px-4 mt-2" onClick={() => setSearchValue('')}>Clear Search</button>
                    </div>
                )}
            </div>
        </div>
    );
};

export default CompanyConfiguration;
